import React, {
  useState,
  useEffect
} from 'react';

import moment from 'moment';

import { makeStyles } from '@material-ui/core/styles';
import {
  Divider,
  Collapse,
  Badge,
  Button,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  StepContent,
  Typography,
  Tooltip,
  colors,
  ButtonBase,
  Paper,
  ListSubheader,
} from '@material-ui/core';

import AddIcon from '@material-ui/icons/Add';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import AdjustOutlinedIcon from '@material-ui/icons/AdjustOutlined';
import DeleteIcon from '@material-ui/icons/Delete';

import {
  FileComment,
  Document,
  Comment,
} from '../../../types';
import {
  DocumentView,
  AnonymousUserView,
  StatusChip,
  DateTimeView,
  ProfileChip
} from '../singles';

import localization from '../../../utils/localizations';
import lastStatus from '../../../utils/lastStatus';
import {
  BaseAPI,
} from '../../../data/BaseAPI';

const useStyles = makeStyles((theme) => ({
  mainBody: {
    padding: 10,
    marginTop: 20,
    marginBottom: 20,
  },
  secondPanel: {
    padding: 10,
    marginTop: 20,
    marginBottom: 20,
  },
  pageTitle: {
    marginTop: 10,
    marginBottom: 10,
  },
  paddingTopBottom: {
    paddingTop: 10,
    paddingBottom: 10,
  },
  consultationContainer: {
    marginTop: 20,
    marginBottom: 30,
  },
  marginRight: {
    marginRight: 10,
  },
  statusReason: {
    whiteSpace: 'pre-wrap',
  },
  reasonContainer: {
    paddingLeft: 10,
    borderLeft: "solid 2px",
    borderLeftColor: colors.blue[500],
  },
  marginVertical: {
    marginTop: 10,
    marginBottom: 10
  },
  comment: {
    display: 'block',
    width: '100%',
    maxWidth: '600px',
    padding: theme.spacing(1),
    textAlign: 'left',
  },
  commentBody: {
    textAlign: 'left',
    marginBottom: theme.spacing(1),
  },
  commentPageNumber: {
    textAlign: 'right',
    marginTop: theme.spacing(1),
  },
  highlightedPreview: {
    width: '100%',
    border: 'dashed 1px grey',
  },
  inlineFlexMargin: {
    display: 'inline-flex',
    marginRight: theme.spacing(1),
  },
  commentQuoted: {
    color: '#777',
    paddingLeft: theme.spacing(1),
    borderLeft: 'solid 1px',
  }
}));


function FileCommentsHistory(props: {documents: Document[]; reviewerComments?: Comment[]; collapsed?: boolean; hideIfEmpty?: boolean;}) {
  const classes = useStyles();
  const [isCollapsed, setIsCollapsed] = useState(props.collapsed ? true : false);

  let comments: {doc?: Document, comment?: FileComment, userComment?: Comment}[] = [];
  props.documents.forEach((document) => {
    document.comments.forEach(comment => {
      comments.push({
        doc: document,
        comment: comment
      })
    });
  });

  if (props.reviewerComments) {
    props.reviewerComments.forEach((comment) => {
      comments.push({
        doc: undefined,
        userComment: comment
      });
    });
  }

  comments = comments.sort((c1, c2) => {
    const name1 = c1.comment ? c1.comment.created_at : (c1.userComment ? c1.userComment.created_at : 0);
    const name2 = c2.comment ? c2.comment.created_at : (c2.userComment ? c2.userComment.created_at : 0);
    if (name1 > name2) { return -1; }
    if (name1 < name2) { return 1; }
    return 0;
  });

  if (props.hideIfEmpty && comments.length === 0) return (<div />);

  return (
    <Paper className={classes.mainBody}>
      <ListSubheader style={{paddingLeft: 8}}>{localization.commentsHistory}</ListSubheader>
      <Collapse in={isCollapsed}>
        <Badge badgeContent={comments.length} color="secondary">
          <Button variant="contained" size="small" onClick={() => setIsCollapsed(false)}>{localization.show}</Button>
        </Badge>
      </Collapse>
      <Collapse in={!isCollapsed}>
        {!isCollapsed &&
        <Stepper orientation="vertical">
        {comments.map(({doc, comment, userComment}, i) => {
          if (!comment && !userComment) return (<div />);
          if (comment) {
            return (
              <Step key={`comment-${comment.id}`} active>
                <StepLabel StepIconComponent={AdjustOutlinedIcon}>
                  <AnonymousUserView
                    id={comment.user?.id}
                    className={classes.inlineFlexMargin}
                  />
                  <DateTimeView
                    value={comment.created_at}
                    fromNow
                    chip
                    size="small"
                    className={classes.marginRight}
                  />
                </StepLabel>
                <StepContent>
                  <div
                    className={classes.comment}
                  >
                    <Typography
                      variant="body1"
                      color="textPrimary"
                      className={classes.commentBody}
                    >
                      {comment.comment}
                    </Typography>
                    <Typography
                      variant="body2"
                      className={classes.commentQuoted}
                    >
                      {comment.highlighted_text?.trim()}
                    </Typography>
                    {comment.highlighted_preview && <img src={comment.highlighted_preview} className={classes.highlightedPreview} />}
                    {comment.highlighted_position?.pageNumber &&
                    <div className={classes.commentPageNumber}>Page {comment.highlighted_position.pageNumber}</div>}
                  </div>
                  {!!doc && <DocumentView doc={doc} hideBadge />}
                </StepContent>
              </Step>
            );
          }
          if (userComment) {
            return (
              <Step key={`comment-${userComment.id}`} active>
                <StepLabel StepIconComponent={AdjustOutlinedIcon}>
                  <AnonymousUserView
                    id={userComment.user?.id}
                    className={classes.inlineFlexMargin}
                  />
                  <DateTimeView
                    value={userComment.created_at}
                    fromNow
                    chip
                    size="small"
                    className={classes.marginRight}
                  />
                </StepLabel>
                <StepContent>
                  <div
                    className={classes.comment}
                  >
                    <Typography
                      variant="body1"
                      color="textPrimary"
                      className={classes.commentBody}
                    >
                      {userComment.comment}
                    </Typography>
                  </div>
                </StepContent>
              </Step>
            );
          }
        })}
        </Stepper>}
        <Button onClick={() => setIsCollapsed(true)}>{localization.close}</Button>
      </Collapse>
    </Paper>
  );
}

export {FileCommentsHistory};
